import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { AlertController, IonicModule, ModalController, NavController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import * as _ from 'lodash';
import moment from 'moment';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { EditAutomationFunctionsComponent } from 'src/app/components/edit-automation-functions/edit-automation-functions.component';
import {
  AUTOMATION_ACTION_TYPES_ICONS,
  AUTOMATION_ACTION_TYPES_LABELS,
  FEATURES,
  PROJECT_MEMBER_ROLES,
} from 'src/app/constants';
import { Automation, AUTOMATION_ACTION_TYPES, AUTOMATION_STEP_TYPES, WhereClause } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { AutomationsService } from 'src/services/automations.service';
import { MessageService } from 'src/services/message.service';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    SharedPipesModule,
    RouterModule,
    NgxTippyModule,
  ],
  selector: 'app-automations-list',
  templateUrl: './automations-list.component.html',
  styleUrls: ['./automations-list.component.scss'],
})
export class AutomationsListComponent implements OnInit, OnDestroy {
  automations: Partial<Automation>[] = [];
  filteredAutomations: Partial<Automation>[] = [];
  canEditAutomations: boolean = false;

  searchText: string;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  pageSize = 50;
  routerSub: Subscription;
  automationsEnabled: boolean = false;
  featureEnabled: boolean = false;
  selectedAutomationId: string | null = null;
  selectedAutomation: Partial<Automation>;
  isLoadingAutomationRunLogs: boolean = false;
  showAutomationAddSecretModal = false;
  automationSecrets: any[];
  automationSecret = null;
  functionsVersions: any[];
  functions: any[];
  automationStores: any[];
  showAutomationAddStoreModal = false;
  automationStore = null;
  automationRunCountLastMonth: number;
  automationRunCountThisMonth: number;

  projectId: string | null = null;

  tab: 'list' | 'configuration' | 'usage' = 'list';

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private automationsService: AutomationsService,
    private msgSrvc: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private apiService: ApiService,
    private navCtrl: NavController,
    private alertCtrl: AlertController,
  ) {}

  async ngOnInit() {
    this.projectId = this.authService.selectedProject?.id ?? null;
    this.automationsEnabled = this.authService.tenant?.isCustomActionsEnabled;
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.AUTOMATIONS);
    this.canEditAutomations =
      this.authService.isTenantAdmin ||
      this.authService.projects.some(
        (p) => p.role === PROJECT_MEMBER_ROLES.OWNER || p.role === PROJECT_MEMBER_ROLES.AGENT,
      );

    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(async () => {
        this.isLoading = true;

        if (this.automationsEnabled && this.featureEnabled) {
          await Promise.all([
            this.getAutomations(),
            this.getAutomationSecrets(),
            this.getFunctionsVersions(),
            this.getAutomationStores(),
          ]);
        }

        this.isLoading = false;
      });

    this.route.queryParams.subscribe(async (params) => {
      if (params.view === 'configuration') {
        this.tab = 'configuration';
      } else if (params.view === 'usage') {
        this.tab = 'usage';
      } else {
        this.tab = 'list';
      }
    });
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async getAutomations() {
    try {
      const automationsResponse = await this.automationsService.list({
        limit: null,
        order: ['startedAt'],
        descending: false,
        where: [
          {
            field: 'projectId',
            operator: '==',
            value: this.projectId,
          },
        ],
      });

      this.automations = automationsResponse.data;
      this.filteredAutomations = this.automations;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async getFunctionsVersions() {
    try {
      const functionsVersionsResponse: any = await this.automationsService.getAllFunctionsVersions(this.projectId);

      this.functionsVersions = functionsVersionsResponse;

      const activeFunc = this.getFunctionsVersion('active');
      const functionNames = activeFunc.exportedFunctionNames || [];
      this.functions = functionNames.map((f) => {
        return {
          name: f,
          version: activeFunc.version,
        };
      });
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async getAutomationRuns() {
    try {
      const [lastMonth, thisMonth] = await Promise.all([
        this.automationsService.listRunsForAutomation(null, {
          limit: 1, // we only need total count
          order: ['startedAt'],
          descending: false,
          where: [
            {
              field: 'startedAt',
              operator: '>=',
              value: moment().subtract(1, 'month').startOf('month').toISOString(),
            },
            {
              field: 'startedAt',
              operator: '<=',
              value: moment().subtract(1, 'month').endOf('month').toISOString(),
            },
            {
              field: 'projectId',
              operator: '==',
              value: this.projectId,
            },
          ],
        }),
        this.automationsService.listRunsForAutomation(null, {
          limit: 1, // we only need total count
          order: ['startedAt'],
          descending: false,
          where: [
            {
              field: 'startedAt',
              operator: '>=',
              value: moment().startOf('month').toISOString(),
            },
            {
              field: 'projectId',
              operator: '==',
              value: this.projectId,
            },
          ],
        }),
      ]);

      this.automationRunCountLastMonth = lastMonth.totalCount;
      this.automationRunCountThisMonth = thisMonth.totalCount;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  showNextPage() {
    this.pageCount += 1;
  }

  handleFilters() {
    this.filteredAutomations = this.automations;

    const term = this.searchText?.trim().toLowerCase();

    if (term) {
      this.filteredAutomations = this.automations.filter((automation) => automation.name.toLowerCase().includes(term));
    }
  }

  getFunctionsVersion(status: string) {
    return (this.functionsVersions ?? []).find((version) => version.status === status);
  }

  async editFunctions() {
    const customActionsVersion = this.getFunctionsVersion('draft') ?? this.getFunctionsVersion('active');

    const modal = await this.modalCtrl.create({
      component: EditAutomationFunctionsComponent,
      cssClass: 'modal-full-screen',
      componentProps: {
        version: customActionsVersion.version,
        status: customActionsVersion.status,
        projectId: this.projectId,
      },
      keyboardClose: false,
      backdropDismiss: false,
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getFunctionsVersions();
    });
  }

  async createAutomation() {
    this.navCtrl.navigateForward(`/dashboard/automations/new`);
  }

  async editAutomation(id: string) {
    if (!this.canEditAutomations) {
      this.msgSrvc.show(`You don't have permission to edit automations`);
      return;
    }

    this.navCtrl.navigateForward(`/dashboard/automations/${id}`);
  }

  async enableAutomations() {
    this.isLoading = true;

    try {
      await this.apiService.postPromise('/me/tenant/enable-custom-actions', {});

      this.automationsEnabled = true;

      await Promise.all([this.getAutomations()]);
    } catch (err) {
      this.msgSrvc.showError(err);
    }

    this.isLoading = false;
  }

  async getAutomationSecrets() {
    try {
      const secretsResponse = await this.automationsService.getSecretNames(this.projectId);

      this.automationSecrets = secretsResponse.map((secret) => {
        return {
          name: secret,
        };
      });
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async getAutomationStores() {
    try {
      const storesResponse: any = await this.automationsService.getStores(this.projectId);

      this.automationStores = storesResponse;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async deleteAutomationSecret(automationSecret: any) {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure you want to delete this secret?',
      buttons: [
        {
          text: 'No',
        },
        {
          text: 'Yes',
          handler: async () => {
            await this.automationsService.deleteSecret(automationSecret.name, this.projectId);
            await this.getAutomationSecrets();

            this.msgSrvc.show('Automation secret deleted successfully');
          },
        },
      ],
    });

    alert.present();
  }

  onDismissAutomationAddSecretModal() {
    this.showAutomationAddSecretModal = false;
  }

  openAddSecretModal() {
    this.automationSecret = {};
    this.showAutomationAddSecretModal = true;
  }

  async addAutomationSecret() {
    if (!this.automationSecret.name || !this.automationSecret.value) {
      this.msgSrvc.show('Please enter a name and value for the secret');
      return;
    }

    await this.automationsService.addSecret(this.automationSecret.name, this.automationSecret.value, this.projectId);
    await this.getAutomationSecrets();

    this.msgSrvc.show('Automation secret added successfully');
    this.showAutomationAddSecretModal = false;

    this.automationSecret = null;
  }

  requestAccess() {
    window.open(`mailto:sales@unthread.io?subject=Beta+Access+to+Automations`, '_blank');
  }

  async deleteAutomationStore(storeName: string) {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure you want to delete this key-value store?',
      buttons: [
        {
          text: 'No',
        },
        {
          text: 'Yes',
          handler: async () => {
            await this.automationsService.deleteStore(storeName, this.projectId);
            await this.getAutomationStores();

            this.msgSrvc.show('Automation store deleted successfully');
          },
        },
      ],
    });

    alert.present();
  }

  onDismissAutomationAddStoreModal() {
    this.showAutomationAddStoreModal = false;
  }

  openAddStoreModal() {
    this.automationStore = {};
    this.showAutomationAddStoreModal = true;
  }

  async addAutomationStore() {
    if (!this.automationStore.name) {
      this.msgSrvc.show('Please enter a name and value for the store');
      return;
    }

    await this.automationsService.addStore(
      {
        name: this.automationStore.name,
        description: this.automationStore.description,
      },
      this.projectId,
    );
    await this.getAutomationStores();

    this.msgSrvc.show('Automation store added successfully');
    this.showAutomationAddStoreModal = false;

    this.automationStore = null;
  }

  formatTriggerEvent(triggerEvent: string) {
    return _.snakeCase(triggerEvent).replace(/_/g, ' ');
  }

  getStepIcon(data: any) {
    if (data.type === AUTOMATION_STEP_TYPES.BRANCHES) {
      return 'git-branch';
    }

    const type = data?.data?.action?.type;
    return AUTOMATION_ACTION_TYPES_ICONS[type] ?? 'build-outline';
  }

  getStepLabel(data: any) {
    if (data.type === AUTOMATION_STEP_TYPES.BRANCHES) {
      return 'Branching logic';
    }

    const type = data?.data?.action?.type;
    if (type === AUTOMATION_ACTION_TYPES.TRIGGER_FUNCTION && data?.data?.action?.data?.functionName) {
      return `Custom Function (${data?.data?.action?.data?.functionName})`;
    }

    return AUTOMATION_ACTION_TYPES_LABELS[type] ?? 'Custom Function';
  }

  selectTab(tab: 'list' | 'configuration' | 'usage') {
    this.tab = tab;
    this.navCtrl.navigateForward(this.router.url, {
      queryParams: {
        view: tab,
      },
    });

    if (tab === 'usage') {
      this.getAutomationRuns();
    }
  }
}
